import { adminApi } from '@/api';
import { queryKeys } from '@/api/query-keys';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { SyncRunFilters, SyncRunResult, SyncRunsResponse } from './admin.types';

function useSyncMutation(
  mutationFn: () => Promise<SyncRunResult>,
  options?: UseMutationOptions<SyncRunResult, AxiosError, void>,
) {
  const queryClient = useQueryClient();
  return useMutation<SyncRunResult, AxiosError, void>({
    mutationFn,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.syncRuns });
      queryClient.invalidateQueries({ queryKey: queryKeys.syncRunsLatest });
      options?.onSuccess?.(...args);
    },
    ...options,
  });
}

export const useSyncBusinessPartners = (
  options?: UseMutationOptions<SyncRunResult, AxiosError, void>,
) => useSyncMutation(() => adminApi.syncBusinessPartners(), options);

export const useSyncEmployees = (
  options?: UseMutationOptions<SyncRunResult, AxiosError, void>,
) => useSyncMutation(() => adminApi.syncEmployees(), options);

export const useSyncItems = (
  options?: UseMutationOptions<SyncRunResult, AxiosError, void>,
) => useSyncMutation(() => adminApi.syncItems(), options);

export const useSyncShippingTypes = (
  options?: UseMutationOptions<SyncRunResult, AxiosError, void>,
) => useSyncMutation(() => adminApi.syncShippingTypes(), options);

export const useSyncSapOrders = (
  options?: UseMutationOptions<SyncRunResult, AxiosError, void>,
) => useSyncMutation(() => adminApi.syncSapOrders(), options);

export const useComputeItemKpis = (
  options?: UseMutationOptions<SyncRunResult, AxiosError, void>,
) => useSyncMutation(() => adminApi.computeItemKpis(), options);

export const useLatestSyncRuns = () =>
  useQuery<SyncRunResult[]>({
    queryKey: queryKeys.syncRunsLatest,
    queryFn: () => adminApi.getLatestSyncRuns(),
    refetchInterval: 60_000,
  });

export const useSyncRuns = (filters?: SyncRunFilters) =>
  useQuery<SyncRunsResponse>({
    queryKey: [...queryKeys.syncRuns, filters],
    queryFn: () => adminApi.getSyncRuns(filters),
  });
